import AccountContext from 'context/AccountContext';
import useOngoingMultipleSession from 'hooks/useOngoingMultipleSession';
import React, { useContext, useEffect, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { useParkingStore } from 'stores/parkingStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  ChargingSession,
  Megaflex,
  ParkingSession,
} from 'types/generatedSchemaTypes';
import { isItemParking } from 'utils/parkUtils';
import OngoingMultipleSessionItem from './OngoingMultipleSessionItem';
import { useUiStore } from 'stores/uiStore';

const OngoingMultipleSessionsModalContent = () => {
  const [timeNow, setTimeNow] = useState(new Date());
  const { parkAndChargeData } = useOngoingMultipleSession();
  const {
    customer: { vehicles },
    customer: { megaflex },
  } = useContext(AccountContext) || [];

  const hasMifareSession = (sessions: ParkingSession[]) => {
    return sessions.some(
      (session) => session?.accessDevice?.equipmentType === 'MIFARE'
    );
  };

  const extractAccessDevices = (sessions: ParkingSession[]) => {
    return sessions
      .map((session) => session?.accessDevice?.value)
      .filter(
        (value): value is string => value !== undefined && value !== null
      );
  };

  const extractCustomerMegaFlex = (megaflex: Megaflex) => {
    return (
      megaflex?.tokens
        ?.map((token) => token?.serialNumber)
        .filter(
          (value): value is string => value !== undefined && value !== null
        ) ?? []
    );
  };

  const displayMegaFlexValue = (
    sessions: ParkingSession[],
    megaflex: Megaflex
  ): string | undefined => {
    const accessDevices = extractAccessDevices(sessions);
    const customerMegaFlexTokens = extractCustomerMegaFlex(megaflex);

    const matchingDevice = accessDevices.find((device) =>
      customerMegaFlexTokens?.includes(device)
    );

    return matchingDevice ?? undefined;
  };

  const megaflexValue =
    megaflex && hasMifareSession(parkAndChargeData)
      ? displayMegaFlexValue(parkAndChargeData, megaflex)
      : '';

  useEffect(() => {
    // if screen is mounted, update every 15 secs
    const handler = setInterval(() => {
      setTimeNow(new Date());
    }, 15000);

    return () => {
      clearInterval(handler);
    };
  }, [setTimeNow]);

  const handleSelectSession = (item: ParkingSession | ChargingSession) => {
    const type = isItemParking(item) ? 'PARKING' : 'CHARGING';

    const sessionId = isItemParking(item)
      ? item.sessionId
      : item.chargingSessionId;
    useParkingStore.getState().setSelectedOngoingSession(type);
    useParkingStore
      .getState()
      .setSelectedSessionIdFromMultiple(sessionId ?? null);
    useUiStore.getState().setSheetModal('');
  };

  return (
    <StyledWrapper mb={50}>
      <ScrollView>
        {parkAndChargeData.map((item: ParkingSession | ChargingSession) => {
          const id = isItemParking(item)
            ? item.sessionId
            : item.chargingSessionId;
          return (
            <OngoingMultipleSessionItem
              key={`ongoing-multiple-item-${id}`}
              id={id}
              session={item}
              handlePress={handleSelectSession}
              currentTime={timeNow}
              vehicles={vehicles}
              megaflexValue={megaflexValue}
            />
          );
        })}
      </ScrollView>
    </StyledWrapper>
  );
};

export default OngoingMultipleSessionsModalContent;
